import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  Dimensions,
  TouchableOpacity,
  Pressable,
  Text,
  ScrollView,
  Switch,
  Alert,
  Linking,
  TextInput as RNTextInput,
} from 'react-native';
import Tooltip from 'react-native-walkthrough-tooltip';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { useTour } from '../../context/TourContext';
import Sidebar from '../shared/Sidebar';
import { shouldResetHomeSubContent } from '../../utils/mobileTabNavigation';
import TopNavigationBar from '../shared/TopNavigationBar';
import { LinearGradient } from 'expo-linear-gradient';
import { useAuth } from '../../hooks/useAuth';
import TextField from '../common/TextField';
import Button from '../common/Button';
import EyeIcon from '../common/EyeIcon';
import SystemAnnouncementBanner from '../shared/SystemAnnouncementBanner';
import SupportForm from '../shared/SupportForm';
import { TourTooltipContent } from '../shared';
import { trackButtonClick } from '../../utils/analytics';
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '../../constants/layout';

// Import screens for content switching
import HomeScreen from '../../screens/home/HomeScreen';
import MoodBoardScreen from '../../screens/moodboard/MoodBoardScreen';
import SmartWardrobeScreen from '../../screens/smart-wardrobe/SmartWardrobeScreen';
import ColourAnalysisScreen from '../../screens/color-analysis/ColourAnalysisScreen';
import VirtualDrapeTestScreen from '../../screens/virtual-drape/VirtualDrapeTestScreen';
import ProfileScreen from '../../screens/profile/ProfileScreen';
import StyleConsultationDetailScreen from '../../screens/style-consultation/StyleConsultationDetailScreen';
import ColorGenerator from '../shared/ColorGenerator';
import ColorLibraryScreen from '../../screens/color-library/ColorLibraryScreen';
import PageHero from '../shared/PageHero';

interface WebLayoutProps {
  children?: React.ReactNode;
  activeTab?: string;
  onTabPress?: (tab: string) => void;
  activeContent?: string;
  onContentChange?: (content: string) => void;
  onSettingsCategoryChange?: (category: string) => void;
  activeSettingsCategory?: string;
  routeParams?: Record<string, unknown>;
}

const WebLayout: React.FC<WebLayoutProps> = ({
  children,
  activeTab = 'HomeTab',
  onTabPress,
  activeContent,
  onContentChange,
  onSettingsCategoryChange,
  activeSettingsCategory,
  routeParams,
}) => {
  const { colors } = useTheme();
  const { user, signOut, isLoading, changePassword } = useAuth();
  const {
    tourStep,
    totalSteps,
    currentStepConfig,
    nextStep,
    skipTour,
    startTour,
  } = useTour();
  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;
  const isWebLayout = Platform.OS === 'web' && screenWidth >= 768;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [internalActiveContent, setInternalActiveContent] = useState('home');
  const [internalActiveSettingsCategory, setInternalActiveSettingsCategory] =
    useState('account-info');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [supportModalVisible, setSupportModalVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accountInfoAction, setAccountInfoAction] = useState<string | null>(
    null
  );
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [fromBadge, setFromBadge] = useState<{ [key: string]: boolean }>({});

  // Use external content state if provided, otherwise use internal state
  const currentContent = activeContent || internalActiveContent;

  const setCurrentContent = (content: string) => {
    // Extract fromBadge flag if present
    const [contentValue, query] = content.split('?');
    const hasFromBadge = query?.includes('fromBadge=true');
    const finalContent = contentValue || content;

    // Store fromBadge flag for this content
    if (hasFromBadge) {
      setFromBadge((prev) => ({ ...prev, [finalContent]: true }));
    }

    if (onContentChange) {
      onContentChange(finalContent);
    } else {
      setInternalActiveContent(finalContent);
    }
  };

  const resetPasswordFormState = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Handle tab press - reset content to home when switching to HomeTab
  const handleTabPress = (tab: string) => {
    if (shouldResetHomeSubContent(tab)) {
      setCurrentContent('home');
      setFromBadge({});
    }
    // Call the parent onTabPress handler
    if (onTabPress) {
      onTabPress(tab);
    }
  };

  // Reset content to home when activeTab changes to HomeTab (from URL navigation, etc.)
  useEffect(() => {
    if (activeTab === 'HomeTab') {
      const contentToCheck = activeContent || internalActiveContent;
      if (contentToCheck !== 'home') {
        setCurrentContent('home');
        setFromBadge({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setCurrentContent is stable; activeContent/internalActiveContent intentionally not in deps to avoid loops
  }, [activeTab]);

  // Use external settings category state if provided, otherwise use internal state
  const currentSettingsCategory =
    activeSettingsCategory || internalActiveSettingsCategory;
  const setCurrentSettingsCategory = (category: string) => {
    if (category !== 'change-password') {
      resetPasswordFormState();
    }
    if (category !== 'account-info') {
      setAccountInfoAction(null);
    }
    if (onSettingsCategoryChange) {
      onSettingsCategoryChange(category);
    } else {
      setInternalActiveSettingsCategory(category);
    }
  };

  const handleSidebarCollapse = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };

  const handleTopNavSettingsPress = () => {
    // Trigger the same settings flow as the sidebar
    if (onTabPress) {
      onTabPress('SettingsTab');
    }
  };

  const handleTopNavLogoutPress = async () => {
    try {
      await signOut();
    } catch {
      // Logout failed silently
    }
  };

  const handleTopNavLogoPress = () => {
    if (onTabPress) {
      onTabPress('HomeTab');
    }
  };

  const handleTopNavProfilePress = () => {
    if (onTabPress) {
      onTabPress('ProfileTab');
    }
  };

  const handleFAQToggle = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const handlePasswordFormReset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleWebPasswordChange = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { authAPI } = await import('../../api/auth');
      const { getToken } = await import('../../utils/storage');

      const token = await getToken('auth_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await authAPI.changePassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        token
      );

      if (!response.success) {
        throw new Error(
          response.error || response.detail || 'Failed to change password'
        );
      }
      return true;
    } catch (err: unknown) {
      throw err;
    }
  };

  if (!isWebLayout) {
    return <>{children}</>;
  }

  const renderSettingsDetailsPanel = () => {
    if (!currentSettingsCategory) {
      return (
        <View style={styles.detailsContainer}>
          <Text style={[styles.detailsTitle, { color: colors.text }]}>
            Select a setting to view details
          </Text>
        </View>
      );
    }

    const renderAccountInfoDetails = () => {
      // If a specific action is selected, show that content
      if (accountInfoAction === 'change-password') {
        return renderPasswordDetails();
      }

      if (accountInfoAction === 'deactivate-account') {
        return renderDeactivateAccountDetails();
      }

      // Default account info view
      return (
        <View style={styles.detailsContainer}>
          <Text style={[styles.detailsTitle, { color: colors.text }]}>
            Account Information
          </Text>

          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Email Address
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              {user?.email || 'Not set'}
            </Text>
          </View>

          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Account Status
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Active
            </Text>
          </View>

          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Member Since
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              February 2026
            </Text>
          </View>

          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Last Login
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Today
            </Text>
          </View>

          {/* Divider */}
          <View
            style={[styles.detailsDivider, { backgroundColor: colors.border }]}
          />

          {/* Action Buttons */}
          <Pressable
            style={({ pressed }) => [
              styles.detailsActionItem,
              {
                backgroundColor:
                  hoveredItem === 'change-password'
                    ? colors.primary + '08'
                    : 'transparent',
                borderColor: colors.border,
              },
              hoveredItem === 'change-password' && {
                borderColor: colors.primary + '30',
              },
              pressed && {
                backgroundColor: colors.primary + '15',
              },
            ]}
            onPress={() => setAccountInfoAction('change-password')}
            onHoverIn={() => setHoveredItem('change-password')}
            onHoverOut={() => setHoveredItem(null)}
          >
            <View style={styles.detailsActionContent}>
              <Ionicons
                name="lock-closed-outline"
                size={20}
                color={colors.primary}
              />
              <View style={styles.detailsActionText}>
                <Text
                  style={[styles.detailsActionTitle, { color: colors.text }]}
                >
                  Change Password
                </Text>
                <Text
                  style={[
                    styles.detailsActionSubtitle,
                    { color: colors.secondary },
                  ]}
                >
                  Update your password
                </Text>
              </View>
            </View>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </Pressable>

          <Pressable
            style={({ pressed }) => [
              styles.detailsActionItem,
              {
                backgroundColor:
                  hoveredItem === 'deactivate-account'
                    ? colors.billing + '08'
                    : 'transparent',
                borderColor: colors.border,
              },
              hoveredItem === 'deactivate-account' && {
                borderColor: colors.billing + '30',
              },
              pressed && {
                backgroundColor: colors.billing + '15',
              },
            ]}
            onPress={() => setAccountInfoAction('deactivate-account')}
            onHoverIn={() => setHoveredItem('deactivate-account')}
            onHoverOut={() => setHoveredItem(null)}
          >
            <View style={styles.detailsActionContent}>
              <Ionicons
                name="power-outline"
                size={20}
                color={colors.billing || '#EF4444'}
              />
              <View style={styles.detailsActionText}>
                <Text
                  style={[styles.detailsActionTitle, { color: colors.text }]}
                >
                  Deactivate Account
                </Text>
                <Text
                  style={[
                    styles.detailsActionSubtitle,
                    { color: colors.billing || '#EF4444' },
                  ]}
                >
                  Temporarily disable your account
                </Text>
              </View>
            </View>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </Pressable>
        </View>
      );
    };

    const renderDeactivateAccountDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: '#EF4444' }]}>
          Deactivate Account
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Warning
          </Text>
          <Text style={[styles.detailsValue, { color: '#EF4444' }]}>
            This action will temporarily disable your account
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            What happens when deactivated
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            • You won't be able to log in{'\n'}• Your data will be preserved
            {'\n'}• You can reactivate anytime{'\n'}• No data will be deleted
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Status
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Account is currently active
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() => {
            Alert.alert(
              'Deactivate Account',
              'Are you sure you want to deactivate your account? You can reactivate it anytime by logging in again.',
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Deactivate',
                  style: 'destructive',
                  onPress: () => {
                    Alert.alert(
                      'Coming Soon',
                      'Account deactivation will be available soon.'
                    );
                    setAccountInfoAction(null); // Go back to account info
                  },
                },
              ]
            );
          }}
        >
          <Text style={[styles.detailsSubtitle, { color: '#EF4444' }]}>
            Deactivate Account
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: '#EF4444' }]}>
              Temporarily disable your account
            </Text>
            <Ionicons name="chevron-forward" size={20} color="#EF4444" />
          </View>
        </TouchableOpacity>

        <Pressable
          style={({ pressed }) => [
            styles.detailsActionItem,
            {
              backgroundColor:
                hoveredItem === 'back-to-account'
                  ? colors.primary + '08'
                  : 'transparent',
              borderColor: colors.border,
            },
            hoveredItem === 'back-to-account' && {
              borderColor: colors.primary + '30',
            },
            pressed && {
              backgroundColor: colors.primary + '15',
            },
          ]}
          onPress={() => setAccountInfoAction(null)}
          onHoverIn={() => setHoveredItem('back-to-account')}
          onHoverOut={() => setHoveredItem(null)}
        >
          <View style={styles.detailsActionContent}>
            <Ionicons name="arrow-back" size={20} color={colors.primary} />
            <View style={styles.detailsActionText}>
              <Text style={[styles.detailsActionTitle, { color: colors.text }]}>
                Back to Account Info
              </Text>
              <Text
                style={[
                  styles.detailsActionSubtitle,
                  { color: colors.secondary },
                ]}
              >
                Return to account overview
              </Text>
            </View>
          </View>
          <Ionicons name="chevron-back" size={20} color={colors.secondary} />
        </Pressable>
      </View>
    );

    const renderPasswordDetails = () => {
      const handleChangePassword = async () => {
        // Clear any previous errors
        setPasswordError(null);

        // Validate current password (same as sign-up validation)
        if (!currentPassword.trim()) {
          setPasswordError('Current password is required');
          return;
        }

        if (currentPassword.length < 6) {
          setPasswordError('Current password must be at least 6 characters');
          return;
        }

        // Validate new password (same as sign-up validation)
        if (!newPassword.trim()) {
          setPasswordError('New password is required');
          return;
        }

        if (newPassword.length < 6) {
          setPasswordError('New password must be at least 6 characters');
          return;
        }

        // Validate confirm password (same as sign-up validation)
        if (!confirmPassword.trim()) {
          setPasswordError('Please confirm your new password');
          return;
        }

        if (confirmPassword.length < 6) {
          setPasswordError('Confirm password must be at least 6 characters');
          return;
        }

        if (newPassword !== confirmPassword) {
          setPasswordError("Passwords don't match");
          return;
        }

        // Check if new password is different from current
        if (currentPassword === newPassword) {
          setPasswordError(
            'New password must be different from current password'
          );
          return;
        }

        setIsPasswordLoading(true);

        try {
          // Use the custom web password change function
          await handleWebPasswordChange(currentPassword, newPassword);
          setPasswordSuccess(true);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } catch (err: unknown) {
          const errorMessage =
            (err instanceof Error ? err.message : null) ||
            (err && typeof err === 'object' && 'detail' in err
              ? String((err as { detail?: string }).detail)
              : null) ||
            'Failed to change password. Please try again.';
          setPasswordError(errorMessage);
        } finally {
          setIsPasswordLoading(false);
        }
      };

      if (passwordSuccess) {
        return (
          <View style={styles.detailsContainer}>
            <Text style={[styles.detailsTitle, { color: colors.text }]}>
              Password Changed Successfully
            </Text>
            <View
              style={[
                styles.detailsSection,
                { borderBottomColor: colors.border },
              ]}
            >
              <Text
                style={[styles.detailsSubtitle, { color: colors.secondary }]}
              >
                Status
              </Text>
              <Text style={[styles.detailsValue, { color: colors.primary }]}>
                Password updated successfully
              </Text>
            </View>
            <Pressable
              style={({
                focused,
              }: {
                pressed?: boolean;
                focused?: boolean;
              }) => [
                styles.detailsSection,
                { borderBottomColor: colors.border },
                Platform.OS === 'web' &&
                  focused && {
                    outlineWidth: 2,
                    outlineStyle: 'solid',
                    outlineColor: colors.theme_color || colors.primary,
                    outlineOffset: 2,
                    borderRadius: 8,
                  },
              ]}
              onPress={handlePasswordFormReset}
            >
              <Text
                style={[styles.detailsSubtitle, { color: colors.secondary }]}
              >
                Change Another Password
              </Text>
              <View style={styles.detailsSectionContent}>
                <Text style={[styles.detailsValue, { color: colors.primary }]}>
                  Change password again
                </Text>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.secondary}
                />
              </View>
            </Pressable>
          </View>
        );
      }

      return (
        <View style={styles.detailsContainer}>
          <View style={styles.passwordFormContainer}>
            <Text style={[styles.passwordFormTitle, { color: colors.text }]}>
              Change Password
            </Text>
            <Text
              style={[styles.passwordFormSubtitle, { color: colors.secondary }]}
            >
              Update your password to keep your account secure
            </Text>

            <View style={styles.passwordFormFields}>
              <TextField
                label="Current Password"
                placeholder="Enter current password"
                secureTextEntry={!showCurrentPassword}
                value={currentPassword}
                onChangeText={setCurrentPassword}
                rightIcon={
                  <EyeIcon
                    isVisible={showCurrentPassword}
                    color={colors.secondary}
                  />
                }
                onRightIconPress={() =>
                  setShowCurrentPassword(!showCurrentPassword)
                }
                containerStyle={styles.passwordInputContainer}
              />

              <TextField
                label="New Password"
                placeholder="Enter new password"
                secureTextEntry={!showNewPassword}
                value={newPassword}
                onChangeText={setNewPassword}
                rightIcon={
                  <EyeIcon
                    isVisible={showNewPassword}
                    color={colors.secondary}
                  />
                }
                onRightIconPress={() => setShowNewPassword(!showNewPassword)}
                containerStyle={styles.passwordInputContainer}
                helperText="Password must be at least 6 characters"
              />

              <TextField
                label="Confirm New Password"
                placeholder="Confirm new password"
                secureTextEntry={!showConfirmPassword}
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                rightIcon={
                  <EyeIcon
                    isVisible={showConfirmPassword}
                    color={colors.secondary}
                  />
                }
                onRightIconPress={() =>
                  setShowConfirmPassword(!showConfirmPassword)
                }
                containerStyle={styles.passwordInputContainer}
                error={
                  confirmPassword && newPassword !== confirmPassword
                    ? "Passwords don't match"
                    : undefined
                }
              />

              {passwordError && (
                <View style={styles.passwordErrorContainer}>
                  <Text
                    style={[
                      styles.passwordErrorText,
                      { color: colors.errorText || '#EF4444' },
                    ]}
                  >
                    {passwordError}
                  </Text>
                </View>
              )}

              <Button
                title="Change Password"
                onPress={handleChangePassword}
                loading={isPasswordLoading}
                disabled={
                  !currentPassword.trim() ||
                  !newPassword.trim() ||
                  !confirmPassword.trim()
                }
                style={styles.passwordSubmitButton}
              />
            </View>
          </View>

          <Pressable
            style={({
              pressed,
              focused,
            }: {
              pressed?: boolean;
              focused?: boolean;
            }) => [
              styles.detailsActionItem,
              {
                backgroundColor:
                  hoveredItem === 'back-to-account-password'
                    ? colors.primary + '08'
                    : 'transparent',
                borderColor: colors.border,
              },
              hoveredItem === 'back-to-account-password' && {
                borderColor: colors.primary + '30',
              },
              pressed && {
                backgroundColor: colors.primary + '15',
              },
              Platform.OS === 'web' &&
                focused && {
                  outlineWidth: 2,
                  outlineStyle: 'solid',
                  outlineColor: colors.theme_color || colors.primary,
                  outlineOffset: 2,
                },
            ]}
            onPress={() => setAccountInfoAction(null)}
            onHoverIn={() => setHoveredItem('back-to-account-password')}
            onHoverOut={() => setHoveredItem(null)}
          >
            <View style={styles.detailsActionContent}>
              <Ionicons name="arrow-back" size={20} color={colors.primary} />
              <View style={styles.detailsActionText}>
                <Text
                  style={[styles.detailsActionTitle, { color: colors.text }]}
                >
                  Back to Account Info
                </Text>
                <Text
                  style={[
                    styles.detailsActionSubtitle,
                    { color: colors.secondary },
                  ]}
                >
                  Return to account overview
                </Text>
              </View>
            </View>
            <Ionicons name="chevron-back" size={20} color={colors.secondary} />
          </Pressable>
        </View>
      );
    };

    const renderBillingDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Billing & Subscription
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Plan
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Free Plan
          </Text>
        </View>

        {/* Upgrade Plan button commented out for now */}
        {/* <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert('Coming Soon', 'Plan upgrade will be available soon.')
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Upgrade Plan
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Upgrade to Premium
            </Text>
            <Ionicons
              name='chevron-forward'
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity> */}

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Payment methods will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Payment Methods
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Manage payment methods
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderCurrentPlanDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Current Plan Details
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Plan Name
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Free Plan
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Status
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Active
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Features
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Basic colour analysis, limited mood boards
          </Text>
        </View>

        {/* Upgrade button commented out for now */}
        {/* <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert('Coming Soon', 'Plan upgrade will be available soon.')
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Upgrade
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Get Premium features
            </Text>
            <Ionicons
              name='chevron-forward'
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity> */}
      </View>
    );

    const renderThemeDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Theme Settings
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Theme
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Light
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Theme selection will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Change Theme
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Choose Light, Dark, or System
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderNotificationsDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Notification Settings
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Push Notifications
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Enabled
            </Text>
            <Switch
              value={true}
              onValueChange={() => {}}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Email Notifications
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Disabled
            </Text>
            <Switch
              value={false}
              onValueChange={() => {}}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>
        </View>
      </View>
    );

    // Additional detail renderers for missing sub-categories
    const renderProfileDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Profile Information
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Display Name
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            John Doe
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Bio
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Colour enthusiast and fashion lover
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Profile editing will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Edit Profile
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Update your profile information
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderSecurityDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Account Security
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Two-Factor Authentication
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Disabled
            </Text>
            <Switch
              value={false}
              onValueChange={() => {}}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert('Coming Soon', 'Login history will be available soon.')
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Login History
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              View recent login activity
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Device management will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Device Management
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Manage connected devices
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderPrivacyDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Data & Privacy
        </Text>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert('Coming Soon', 'Data download will be available soon.')
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Download Your Data
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Export your personal data
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Privacy settings will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Privacy Settings
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Configure privacy preferences
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert('Coming Soon', 'Data deletion will be available soon.')
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Delete Data
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Remove specific data types
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderDeleteAccountDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: '#EF4444' }]}>
          Delete Account
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Warning
          </Text>
          <Text style={[styles.detailsValue, { color: '#EF4444' }]}>
            This action cannot be undone
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            What will be deleted
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            All your data, preferences, and settings
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() => {
            Alert.alert(
              'Delete Account',
              'Are you sure you want to permanently delete your account? This action cannot be undone.',
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Delete',
                  style: 'destructive',
                  onPress: () =>
                    Alert.alert(
                      'Coming Soon',
                      'Account deletion will be available soon.'
                    ),
                },
              ]
            );
          }}
        >
          <Text style={[styles.detailsSubtitle, { color: '#EF4444' }]}>
            Delete Account
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: '#EF4444' }]}>
              Permanently delete your account
            </Text>
            <Ionicons name="chevron-forward" size={20} color="#EF4444" />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderUpgradeDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Upgrade Plan
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Plan
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Free Plan
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Recommended Plan
          </Text>
          <Text style={[styles.detailsValue, { color: colors.primary }]}>
            Premium Plan
          </Text>
        </View>

        {/* Upgrade to Premium button commented out for now */}
        {/* <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert('Coming Soon', 'Plan upgrade will be available soon.')
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Upgrade to Premium
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Get unlimited access to all features
            </Text>
            <Ionicons
              name='chevron-forward'
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity> */}
      </View>
    );

    const renderPaymentMethodsDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Payment Methods
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Payment Method
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            No payment method added
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Payment method addition will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Add Payment Method
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Add credit card or PayPal
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderBillingHistoryDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Billing History
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Recent Invoices
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            No invoices found
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Billing history will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            View All Invoices
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Access complete billing history
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderCancelSubscriptionDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Cancel Subscription
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Status
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Free Plan (No active subscription)
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Note
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            You can cancel anytime if you have an active subscription
          </Text>
        </View>
      </View>
    );

    const renderLanguageDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Language Settings
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Current Language
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            English
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Language selection will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Change Language
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Select from available languages
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderAccessibilityDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Accessibility Settings
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Text Size
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Medium
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
          onPress={() =>
            Alert.alert(
              'Coming Soon',
              'Accessibility settings will be available soon.'
            )
          }
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Configure Accessibility
          </Text>
          <View style={styles.detailsSectionContent}>
            <Text style={[styles.detailsValue, { color: colors.primary }]}>
              Adjust text size, contrast, and more
            </Text>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.secondary}
            />
          </View>
        </TouchableOpacity>
      </View>
    );

    const renderAboutDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          About ColourSense
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Version
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            1.0.0
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Description
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            A smart colour analysis app that helps you discover your perfect
            colours with AI-powered analysis, virtual draping, and colour
            generation.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Features
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            • AI-powered colour analysis{'\n'}• Virtual draping{'\n'}• Colour
            Generation
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Last Updated
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            February 2026
          </Text>
        </View>
      </View>
    );

    const renderPrivacyPolicyDetails = () => (
      <ScrollView
        style={styles.detailsContainer}
        showsVerticalScrollIndicator={true}
      >
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          ColourSense Privacy Policy
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Last Updated
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            November 25, 2025
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Introduction
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            At ColourSense ("we," "our," or "us"), your privacy matters. This
            Privacy Policy explains how you may choose to provide personal
            information when using our website, app, and related services
            ("Services"), how we use it, and how you can control it. By using
            ColourSense, you agree to the terms below. If you do not agree,
            please do not use our Services.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            1. Information You May Provide
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            When you use ColourSense, you have control over what information you
            share with us. This includes: Account Information (name, email,
            password), Profile and User Content (profile photo, colour palettes,
            mood boards), Skin Tone Information (voluntary sensitive data used
            only with explicit consent), Location Information (optional
            city/country), Payment Information (processed through secure
            third-party providers), Technical and Usage Information (IP address,
            browser details, usage logs), and Cookies and Similar Technologies
            (for login, preferences, and analytics).
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            2. How We Use Information You Provide
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            We use the information you choose to share to: Create and
            personalize your experience, Generate customized colour palettes,
            Enable sharing and saving of your creative work, Manage your account
            and subscription status, Communicate important updates and offers,
            Improve and maintain our Services, and Comply with legal
            obligations. We never sell your personal information to third
            parties.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            3. Sharing Your Information
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            We may share the information you provide with trusted partners who
            support ColourSense, including: Analytics services (e.g., Google
            Analytics with anonymized data), and Email service providers. All
            partners are contractually obligated to protect your data and only
            use it to provide services on our behalf.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            4. Your Privacy Rights
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            You have full control over the information you provide. You can
            access, update, or delete your data anytime through your account
            settings. You may withdraw consent for sensitive information like
            skin tone or location data at any time. Depending on your location,
            you have rights such as data access, correction, deletion,
            portability, and objection to certain data processing. To exercise
            your rights, contact us at support@mail.mycoloursense.com.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            5. California Privacy Rights
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            If you are a California resident, you have additional rights under
            the CCPA and CPRA, including: Knowing what personal information we
            collect and how we use it, Requesting deletion of your personal
            data, Opting out of the sale of your personal information (note: we
            do not sell your data), and Being free from discrimination for
            exercising your privacy rights. For these requests, contact us at
            support@mail.mycoloursense.com.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            6. Children's Privacy
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            ColourSense does not knowingly collect information from children
            under 13 (or under 16 in the EU). If you are a parent or guardian
            and believe a child has provided us with information, please contact
            us so we can promptly delete it.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            7. Data Security
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            We take reasonable measures to protect the information you share
            with us, including: Using HTTPS encryption, Storing passwords
            securely, Limiting access to your data, and Conducting regular
            security reviews. However, no online system is completely secure.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            8. Data Retention
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            We keep the information you provide only as long as necessary to
            offer you our Services, comply with legal obligations, or as
            otherwise outlined in this policy. You can delete your account and
            personal data at any time.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            9. International Data Transfers
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Your information may be transferred to and stored on servers located
            outside your country of residence. ColourSense complies with
            applicable data protection laws such as GDPR and implements
            appropriate safeguards including Standard Contractual Clauses (SCCs)
            and additional security measures. By using ColourSense, you consent
            to the transfer, storage, and processing of your information outside
            your country.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            10. Cookies and Tracking
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            You control cookies and tracking technologies and can adjust your
            preferences or disable them through your browser. When you first
            visit, our cookie banner lets you accept or decline non-essential
            cookies.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            11. Updates to This Policy
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, legal requirements, or the Services we
            offer. When we make significant changes, we will notify you by email
            or through a notice on our Services before the changes take effect.
            We encourage you to review this Privacy Policy regularly.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            12. Contact Us
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            For any questions, concerns, or to exercise your data rights, please
            contact ColourSense: Email: support@mail.mycoloursense.com
          </Text>
        </View>
      </ScrollView>
    );

    const renderDoNotSellDetails = () => (
      <ScrollView
        style={styles.detailsContainer}
        showsVerticalScrollIndicator={true}
      >
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Do Not Sell My Personal Information
        </Text>
        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            California Residents (CCPA)
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            ColourSense does not sell your personal information. Under the
            California Consumer Privacy Act (CCPA), you have the right to opt
            out of the sale of your personal information. Since we do not sell
            personal information, no opt-out action is required.
          </Text>
        </View>
        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Your California Privacy Rights
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            California residents have additional rights including: knowing what
            personal information we collect, requesting deletion of your data,
            opting out of sale (we do not sell), and freedom from discrimination
            for exercising your privacy rights. Contact us at
            support@mail.mycoloursense.com to exercise these rights.
          </Text>
        </View>
      </ScrollView>
    );

    const renderTermsDetails = () => (
      <ScrollView
        style={styles.detailsContainer}
        showsVerticalScrollIndicator={true}
      >
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          ColourSense Terms and Conditions
        </Text>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Last Updated
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            November 25, 2025
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            Introduction
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Welcome to ColourSense ("we," "us," "our"). These Terms and
            Conditions ("Terms") govern your access to and use of our website,
            mobile application, and related services (collectively, the
            "Services"). By accessing or using our Services, you agree to be
            legally bound by these Terms. If you do not agree, please do not use
            the Services.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            1. Eligibility and Acceptance
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            By using the Services, you represent and warrant that you are at
            least 13 years old (or the minimum age required by law in your
            jurisdiction), have full legal capacity to enter into these Terms,
            and agree to comply with all applicable laws and regulations. Your
            access to and use of the Services constitutes your acceptance of
            these Terms and any future amendments.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            2. User Account Registration and Security
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Certain features require registration. You agree to provide true,
            accurate, current, and complete information about yourself during
            registration and maintain the accuracy of this information. You are
            responsible for safeguarding your account credentials and any
            activities or actions under your account. We reserve the right to
            suspend or terminate your account if we suspect fraudulent or
            unauthorized activity, violation of these Terms, or any conduct
            detrimental to our Services.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            3. User Content and Conduct
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            You retain all rights to content you upload or post ("User
            Content"). You grant us a non-exclusive, worldwide, royalty-free
            license to use, modify, reproduce, display, distribute, and promote
            your User Content within the Services and related marketing. You
            agree not to post content that is unlawful, harmful, threatening,
            abusive, harassing, defamatory, vulgar, obscene, libelous, invasive
            of another's privacy, hateful, or racially, ethnically, or otherwise
            objectionable. You are solely responsible for your User Content and
            the consequences of posting it.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            4. Service Use and Restrictions
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            You agree not to use the Services for any unlawful or prohibited
            activities including: Violating any applicable laws or regulations,
            Engaging in fraudulent, deceptive, or harmful activities, Attempting
            to gain unauthorized access to any systems or user accounts, Using
            automated systems, bots, or scrapers without our permission, or
            Transmitting harmful code, viruses, or malicious software. Our
            Services may contain links to third-party websites or services. We
            do not endorse or assume responsibility for any third-party content,
            products, or services.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            5. Subscriptions, Payments, and Billing
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Some Services require payment of subscription fees. By subscribing,
            you agree to pay the fees and taxes associated with your plan.
            Subscriptions are billed in advance on a monthly or annual basis and
            automatically renew unless canceled at least 24 hours before the end
            of the current billing period. All payments for subscriptions and
            services are generally non-refundable, including any fees for
            partially used subscription periods. However, we may consider refund
            requests made within fourteen (14) days from the date of purchase,
            subject to our discretion and compliance with applicable consumer
            protection laws.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            6. Intellectual Property Rights
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            All content, trademarks, logos, software, and materials on the
            Services are owned or licensed by ColourSense and protected by
            applicable intellectual property laws. We grant you a limited,
            revocable, non-exclusive license to access and use the Services for
            personal, non-commercial purposes, subject to these Terms. You may
            not copy, modify, distribute, sell, or lease any part of the
            Services, or reverse engineer or attempt to extract source code or
            underlying ideas.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            7. Privacy and Data Protection
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            Your privacy is important to us. Please see our Privacy Policy for
            details on how we collect, use, and protect your personal
            information.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            8. Disclaimers and Limitation of Liability
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            The Services are provided "as is" without warranties of any kind,
            either express or implied, including merchantability or fitness for
            a particular purpose. We do not warrant that the Services will be
            error-free or uninterrupted. To the fullest extent permitted by law,
            ColourSense and its affiliates are not liable for any indirect,
            incidental, consequential, or punitive damages arising from your use
            of the Services. You agree to indemnify and hold harmless
            ColourSense and its affiliates against any claims arising from your
            breach of these Terms.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            9. Termination and Suspension
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            You may terminate your account at any time via your account
            settings. Termination does not relieve you of any obligations
            incurred prior to termination. We may suspend or terminate your
            account or access for violations of these Terms or any applicable
            law. Upon termination, your right to access the Services ceases. We
            may retain your data as required by law or for legitimate business
            purposes.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            10. Governing Law and Dispute Resolution
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            These Terms are governed by and shall be construed in accordance
            with the laws of the Republic of the Philippines, without regard to
            its conflict of law principles. Any dispute, controversy, or claim
            arising out of or relating to these Terms, or the breach,
            termination, or invalidity thereof, shall be submitted exclusively
            to the courts of the Philippines, and you hereby consent to the
            jurisdiction of such courts.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            11. Changes to Terms
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            We may update these Terms periodically. Significant changes will be
            communicated through email or in-app notifications. Continued use
            after changes means you accept the new Terms.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            12. Miscellaneous
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            These Terms, along with the Privacy Policy and any additional
            policies, constitute the entire agreement between you and
            ColourSense. If any provision of these Terms is found invalid or
            unenforceable, the remaining provisions shall remain in full force
            and effect. Failure to enforce any provision does not constitute a
            waiver of any rights. ColourSense shall not be liable for failure or
            delay due to causes beyond our reasonable control, including natural
            disasters, technical failures, or acts of government.
          </Text>
        </View>

        <View
          style={[styles.detailsSection, { borderBottomColor: colors.border }]}
        >
          <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
            13. Contact Information
          </Text>
          <Text style={[styles.detailsValue, { color: colors.text }]}>
            For questions, disputes, or legal notices, please contact us at:
            Email: support@mail.mycoloursense.com
          </Text>
        </View>
      </ScrollView>
    );

    const renderHelpDetails = () => {
      const faqItems = [
        {
          question: 'How do I get started with colour analysis?',
          answer:
            'Navigate to Colour Analysis in the main menu, take a photo in good lighting with your face clearly visible, and wait for AI to analyze your features. Natural lighting works best for accurate results.',
        },
        {
          question: "What if my colour analysis isn't working?",
          answer:
            'Ensure good lighting, clear face visibility, and high image quality. Try different angles and check your internet connection. Contact support if issues persist.',
        },
        // {
        //   question: 'How do I manage my wardrobe items?',
        //   answer:
        //     'Use the Smart Wardrobe feature to add, organize, and categorize your clothing items. You can create collections and get AI-powered outfit suggestions.',
        // },
        // {
        //   question: 'Can I create moodboards?',
        //   answer:
        //     'Yes! Use the Mood Boards feature to create and organize colour palettes, save inspiration, and build your personal style collection.',
        // },
        {
          question: 'How do I get help with technical issues?',
          answer:
            'Check our troubleshooting guides first, then contact our support team via email. We typically respond within 24 hours.',
        },
      ];

      return (
        <View style={styles.detailsContainer}>
          <Text style={[styles.detailsTitle, { color: colors.text }]}>
            Help & Support
          </Text>

          {/* FAQ Section */}
          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Frequently Asked Questions
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Find answers to common questions
            </Text>
          </View>

          {faqItems.map((item, index) => (
            <View
              key={index}
              style={[
                styles.detailsSection,
                { borderBottomColor: colors.border },
              ]}
            >
              <TouchableOpacity
                style={styles.faqQuestionContainer}
                onPress={() => handleFAQToggle(index)}
              >
                <Text style={[styles.faqQuestion, { color: colors.text }]}>
                  {item.question}
                </Text>
                <Ionicons
                  name={expandedFAQ === index ? 'chevron-up' : 'chevron-down'}
                  size={20}
                  color={colors.secondary}
                />
              </TouchableOpacity>

              {expandedFAQ === index && (
                <View style={styles.faqAnswer}>
                  <Text
                    style={[styles.faqAnswerText, { color: colors.secondary }]}
                  >
                    {item.answer}
                  </Text>
                </View>
              )}
            </View>
          ))}

          {/* Contact Support Section */}
          <TouchableOpacity
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
            onPress={() => setSupportModalVisible(true)}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Contact Support
            </Text>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Text style={[styles.detailsValue, { color: colors.text }]}>
                Get help from our support team
              </Text>
              <Ionicons
                name="chevron-forward"
                size={20}
                color={colors.secondary}
              />
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
            onPress={() => {
              Linking.openURL(
                'mailto:support@mail.mycoloursense.com?subject=Support%20Request'
              );
            }}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Email Support
            </Text>
            <View style={styles.detailsSectionContent}>
              <Text style={[styles.detailsValue, { color: colors.primary }]}>
                support@mail.mycoloursense.com
              </Text>
              <Ionicons
                name="chevron-forward"
                size={20}
                color={colors.secondary}
              />
            </View>
          </TouchableOpacity>

          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Response Time
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Within 24 hours
            </Text>
          </View>

          <View
            style={[
              styles.detailsSection,
              { borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
              Support Hours
            </Text>
            <Text style={[styles.detailsValue, { color: colors.text }]}>
              Mon-Fri, 9 AM - 6 PM EST
            </Text>
          </View>
        </View>
      );
    };

    const renderTutorialDetails = () => (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          Start Tutorial
        </Text>
        <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
          Run the guided tour again to learn about the sidebar, your badges, and
          how to get your colour analysis done.
        </Text>
        <TouchableOpacity
          style={[styles.tutorialButton, { backgroundColor: colors.primary }]}
          onPress={() => {
            trackButtonClick('Start Tutorial', 'sidebar-tutorial', {
              button_type: 'start_tutorial',
            });
            startTour();
            onTabPress?.('HomeTab');
          }}
        >
          <Ionicons name="play-circle" size={22} color="#FFFFFF" />
          <Text style={styles.tutorialButtonText}>Start Tutorial</Text>
        </TouchableOpacity>
      </View>
    );

    // Map categories to their detail renderers
    const detailRenderers: { [key: string]: () => React.ReactElement } = {
      'account-info': renderAccountInfoDetails,
      about: renderAboutDetails,
      help: renderHelpDetails,
      tutorial: renderTutorialDetails,
      'privacy-policy': renderPrivacyPolicyDetails,
      'do-not-sell': renderDoNotSellDetails,
      terms: renderTermsDetails,
    };

    const renderer = detailRenderers[currentSettingsCategory];
    if (renderer) {
      return renderer();
    }

    // Default case for unhandled categories
    return (
      <View style={styles.detailsContainer}>
        <Text style={[styles.detailsTitle, { color: colors.text }]}>
          {currentSettingsCategory.charAt(0).toUpperCase() +
            currentSettingsCategory.slice(1)}{' '}
          Details
        </Text>
        <Text style={[styles.detailsSubtitle, { color: colors.secondary }]}>
          Details for this setting will be available soon.
        </Text>
      </View>
    );
  };

  const renderContent = () => {
    if (activeTab === 'HomeTab') {
      // For HomeTab, render different content based on currentContent
      switch (currentContent) {
        case 'color-analysis':
          // Hide back button if came from badge
          const showBackButtonCA = !fromBadge['color-analysis'];
          return (
            <View style={styles.contentContainer}>
              {showBackButtonCA && (
                <View style={styles.backButtonContainer}>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => {
                      setFromBadge((prev) => ({
                        ...prev,
                        'color-analysis': false,
                      }));
                      setCurrentContent('home');
                    }}
                  >
                    <Ionicons
                      name="arrow-back"
                      size={20}
                      color={colors.primary}
                    />
                    <Text
                      style={[styles.backButtonText, { color: colors.primary }]}
                    >
                      Back to Main
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              <ColourAnalysisScreen onTabPress={onTabPress} />
            </View>
          );
        case 'virtual-drape':
          // Hide back button if came from badge
          const showBackButtonVD = !fromBadge['virtual-drape'];
          return (
            <View style={styles.contentContainer}>
              {showBackButtonVD && (
                <View style={styles.backButtonContainer}>
                  <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => {
                      setFromBadge((prev) => ({
                        ...prev,
                        'virtual-drape': false,
                      }));
                      setCurrentContent('home');
                    }}
                  >
                    <Ionicons
                      name="arrow-back"
                      size={20}
                      color={colors.primary}
                    />
                    <Text
                      style={[styles.backButtonText, { color: colors.primary }]}
                    >
                      Back to Main
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              <VirtualDrapeTestScreen
                initialView={routeParams?.virtualDrapeView}
                initialViewRequestId={routeParams?.virtualDrapeViewRequestId}
              />
            </View>
          );
        case 'smart-wardrobe':
          return (
            <View style={styles.contentContainer}>
              <View style={styles.backButtonContainer}>
                <TouchableOpacity
                  style={styles.backButton}
                  onPress={() => setCurrentContent('home')}
                >
                  <Ionicons
                    name="arrow-back"
                    size={20}
                    color={colors.primary}
                  />
                  <Text
                    style={[styles.backButtonText, { color: colors.primary }]}
                  >
                    Back to Main
                  </Text>
                </TouchableOpacity>
              </View>
              <SmartWardrobeScreen />
            </View>
          );
        case 'style-consultation':
          return (
            <View style={styles.contentContainer}>
              <View style={styles.backButtonContainer}>
                <TouchableOpacity
                  style={styles.backButton}
                  onPress={() => setCurrentContent('home')}
                >
                  <Ionicons
                    name="arrow-back"
                    size={20}
                    color={colors.primary}
                  />
                  <Text
                    style={[styles.backButtonText, { color: colors.primary }]}
                  >
                    Back to Main
                  </Text>
                </TouchableOpacity>
              </View>
              <StyleConsultationDetailScreen />
            </View>
          );
        case 'home':
        default:
          return (
            <HomeScreen
              onContentChange={setCurrentContent}
              onTabPress={onTabPress}
            />
          );
      }
    } else {
      // For other tabs, render normally
      switch (activeTab) {
        // Moodboard hidden for now
        // case 'MoodBoardTab':
        //   return <MoodBoardScreen route={routeParams} />;
        case 'SmartWardrobe':
          return <SmartWardrobeScreen />;
        case 'ColourAnalysis':
          return <ColourAnalysisScreen onTabPress={onTabPress} />;
        case 'VirtualDrapeTest':
          return (
            <VirtualDrapeTestScreen
              initialView={routeParams?.virtualDrapeView}
              initialViewRequestId={routeParams?.virtualDrapeViewRequestId}
            />
          );
        case 'ColorGenerator':
          return <ColorGenerator onTabPress={onTabPress} />;
        case 'ColorLibrary':
          return <ColorLibraryScreen onTabPress={onTabPress} />;
        case 'ProfileTab':
          return <ProfileScreen onTabPress={onTabPress} />;
        case 'SettingsTab':
          return (
            <View style={styles.settingsContainer}>
              <PageHero
                title="Settings"
                subtitle="Account, theme, notifications, and support."
              />
              <View style={styles.breadcrumbContainer}>
                <Text
                  style={[styles.breadcrumbText, { color: colors.secondary }]}
                >
                  Settings {'>'} {getBreadcrumbDetail()}
                </Text>
              </View>
              <View style={styles.settingsLayout}>
                <View style={styles.detailsPanel}>
                  <ScrollView
                    style={styles.detailsScrollView}
                    contentContainerStyle={styles.detailsScrollContent}
                    showsVerticalScrollIndicator={false}
                  >
                    {renderSettingsDetailsPanel()}
                  </ScrollView>
                </View>
              </View>

              <SupportForm
                visible={supportModalVisible}
                onClose={() => setSupportModalVisible(false)}
                onSuccess={() => {
                  Alert.alert(
                    'Success',
                    'Your support request has been submitted. We will get back to you within 24 hours.'
                  );
                  setSupportModalVisible(false);
                }}
              />
            </View>
          );
        default:
          return (
            <HomeScreen
              onContentChange={setCurrentContent}
              onTabPress={onTabPress}
            />
          );
      }
    }
  };

  const getBreadcrumbDetail = () => {
    if (currentSettingsCategory) {
      const detailRenderers: { [key: string]: string } = {
        'account-info': 'Account Information',
        about: 'About ColourSense',
        help: 'Help & Support',
        tutorial: 'Start Tutorial',
        'privacy-policy': 'Privacy Policy',
        'do-not-sell': 'Do Not Sell My Info',
        terms: 'Terms of Service',
      };
      return detailRenderers[currentSettingsCategory] || 'Settings';
    }
    return 'Settings';
  };

  return (
    <View style={[styles.mainContainer, { height: screenHeight }]}>
      {/* System Announcement Banner */}
      <SystemAnnouncementBanner />

      {/* Top Navigation Bar - Fixed at the very top */}
      <View style={styles.topNavContainer}>
        <TopNavigationBar
          onProfilePress={handleTopNavProfilePress}
          onSettingsPress={handleTopNavSettingsPress}
          onLogoutPress={handleTopNavLogoutPress}
          onLogoPress={handleTopNavLogoPress}
        />
      </View>

      {/* Main Layout Container */}
      <View style={[styles.layoutContainer, { height: screenHeight - 60 }]}>
        {/* Web: custom tour overlay so tooltip is visible (library measure/Modal can fail on web) */}
        {Platform.OS === 'web' && currentStepConfig?.id === 'sidebar' ? (
          <>
            <Pressable
              style={[
                styles.tourOverlay,
                {
                  left: sidebarCollapsed
                    ? SIDEBAR_WIDTH_COLLAPSED
                    : SIDEBAR_WIDTH_EXPANDED,
                },
              ]}
              onPress={skipTour}
            />
            <View
              style={[
                styles.tourSidebarHighlight,
                {
                  width: sidebarCollapsed
                    ? SIDEBAR_WIDTH_COLLAPSED
                    : SIDEBAR_WIDTH_EXPANDED,
                },
              ]}
            />
            <View
              style={[
                styles.tourTooltipPosition,
                {
                  left:
                    (sidebarCollapsed
                      ? SIDEBAR_WIDTH_COLLAPSED
                      : SIDEBAR_WIDTH_EXPANDED) + 16,
                },
              ]}
            >
              <TourTooltipContent
                title={currentStepConfig.title}
                onNext={nextStep}
                onSkip={skipTour}
                isLastStep={tourStep === totalSteps}
              />
            </View>
          </>
        ) : null}

        {Platform.OS !== 'web' ? (
          <Tooltip
            isVisible={currentStepConfig?.id === 'sidebar'}
            content={
              currentStepConfig ? (
                <TourTooltipContent
                  title={currentStepConfig.title}
                  onNext={nextStep}
                  onSkip={skipTour}
                  isLastStep={tourStep === totalSteps}
                />
              ) : (
                <View />
              )
            }
            placement={currentStepConfig?.placement || 'right'}
            onClose={skipTour}
            useInteractionManager={true}
          >
            <View
              style={[
                styles.sidebarContainer,
                {
                  width: sidebarCollapsed
                    ? SIDEBAR_WIDTH_COLLAPSED
                    : SIDEBAR_WIDTH_EXPANDED,
                },
              ]}
            >
              <Sidebar
                activeTab={activeTab}
                onTabPress={handleTabPress}
                onCollapseChange={handleSidebarCollapse}
                isCollapsed={sidebarCollapsed}
                onSettingsCategoryChange={setCurrentSettingsCategory}
                activeSettingsCategory={currentSettingsCategory}
              />
            </View>
          </Tooltip>
        ) : (
          <View
            style={[
              styles.sidebarContainer,
              {
                width: sidebarCollapsed
                  ? SIDEBAR_WIDTH_COLLAPSED
                  : SIDEBAR_WIDTH_EXPANDED,
              },
            ]}
          >
            <Sidebar
              activeTab={activeTab}
              onTabPress={handleTabPress}
              onCollapseChange={handleSidebarCollapse}
              isCollapsed={sidebarCollapsed}
              onSettingsCategoryChange={setCurrentSettingsCategory}
              activeSettingsCategory={currentSettingsCategory}
            />
          </View>
        )}

        {/* Main Content Area */}
        <View
          style={[
            styles.content,
            {
              backgroundColor: colors.panelBackground ?? colors.background,
              marginLeft:
                activeTab === 'SettingsTab'
                  ? sidebarCollapsed
                    ? SIDEBAR_WIDTH_COLLAPSED + 320
                    : SIDEBAR_WIDTH_EXPANDED + 320
                  : sidebarCollapsed
                    ? SIDEBAR_WIDTH_COLLAPSED
                    : SIDEBAR_WIDTH_EXPANDED,
              height: screenHeight - 60, // Subtract top navigation bar height
              overflow: 'hidden',
            },
          ]}
        >
          <ScrollView
            style={styles.contentScrollView}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
          >
            {children || renderContent()}
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      overflow: 'hidden',
    }),
  },
  topNavContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1001, // Higher than sidebar
    ...(Platform.OS === 'web' && {
      width: '100%',
    }),
  },
  layoutContainer: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 60, // Account for top navigation bar
    ...(Platform.OS === 'web' && {
      overflow: 'hidden',
    }),
  },
  sidebarContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    zIndex: 1000,
  },
  tourOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2000,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  tourSidebarHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 2001,
    pointerEvents: 'none',
  },
  tourTooltipPosition: {
    position: 'absolute',
    top: 80,
    zIndex: 2002,
    ...(Platform.OS === 'web' && {
      maxWidth: 320,
    }),
  },
  content: {
    flex: 1,
  },
  contentScrollView: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  backButtonContainer: {
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
    alignSelf: 'flex-start',
  },
  backButtonText: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: '500',
  },
  settingsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
    paddingHorizontal: 20,
  },
  settingsSubtitle: {
    fontSize: 14,
    color: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 20,
    lineHeight: 20,
  },
  settingsSection: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  settingsValue: {
    fontSize: 12,
    fontWeight: '400',
  },
  // New styles for the settings details panel
  settingsContainer: {
    flex: 1,
    height: '100%',
  },
  settingsLayout: {
    flexDirection: 'row',
    gap: 0,
    height: '100%',
  },
  detailsPanel: {
    flex: 1,
    paddingLeft: 16,
    paddingRight: 16,
    height: '100%',
  },
  settingsDetailsContainer: {
    flex: 1,
    padding: 16,
  },
  settingsDetailsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
  },
  settingsDetailsSection: {
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  settingsDetailsSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  settingsDetailsValue: {
    fontSize: 12,
    fontWeight: '400',
  },
  settingsDetailsSectionContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  // New styles for details panel
  detailsContainer: {
    padding: 16,
    paddingTop: 16,
  },
  detailsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
  },
  tutorialButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    marginTop: 14,
    alignSelf: 'flex-start',
  },
  tutorialButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  detailsSection: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    marginBottom: 0,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
  },
  detailsSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  detailsValue: {
    fontSize: 12,
    fontWeight: '400',
  },
  detailsSectionContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  detailsScrollView: {
    flex: 1,
  },
  detailsScrollContent: {
    paddingBottom: 14, // Add some padding at the bottom for the last item
  },
  // Enhanced details styles
  detailsDivider: {
    height: 1,
    marginVertical: 12,
    marginHorizontal: 16,
  },
  detailsActionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    transition: 'all 0.2s ease',
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
  },
  detailsActionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  detailsActionText: {
    marginLeft: 12,
    flex: 1,
  },
  detailsActionTitle: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 2,
  },
  detailsActionSubtitle: {
    fontSize: 14,
    fontWeight: '400',
  },
  breadcrumbContainer: {
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  breadcrumbText: {
    fontSize: 14,
    fontWeight: '400',
  },
  // New styles for FAQ and Contact Support sections
  faqQuestionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '500',
    flex: 1,
  },
  faqAnswer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 8,
    marginTop: 4,
  },
  faqAnswerText: {
    fontSize: 14,
    lineHeight: 20,
  },
  // New styles for password change form
  passwordFormContainer: {
    maxWidth: 480, // Constrain width for better readability
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 20,
  },
  passwordFormTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  passwordFormSubtitle: {
    fontSize: 14,
    marginBottom: 32,
    textAlign: 'center',
    lineHeight: 20,
  },
  passwordFormFields: {
    gap: 20, // Space between form fields
  },
  passwordInputContainer: {
    marginVertical: 0, // Remove extra margin since we're using gap
  },
  passwordErrorContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.2)',
  },
  passwordErrorText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  passwordSubmitButton: {
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 2,
  },
  passwordSubmitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  changePasswordButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
  },
  changePasswordButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  errorText: {
    fontSize: 14,
    fontWeight: '500',
    marginTop: 10,
  },
  loadingContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
});

export default WebLayout;
